<template>
  <div>
    <posts-toolbar
      :filters="filters"
      :minimized="minimized"
      :maximize-btn="maximizeBtn"
      @update:filters="onFiltersUpdate"
    >
      <slot />
      <date-range-filter
        v-if="(filters.socialSiteIds || []).length"
        :client-id="client.id"
        :date-of-loss="client.dateOfLoss"
        :model-value="range"
        :filters="filters"
        @update:modelValue="selectRange"
      />
      <template v-slot:append>
        <LabelFilterBtn
          :client-id="client.id"
          :model-value="labelSelection"
          @update:model-value="onLabelSelectionChange"
        />
        <PersonsSidebarBtn
          :client-id="client.id"
          :person-ids="personIds"
          @update:person-ids="onPersonIdsChange"
        />
      </template>
    </posts-toolbar>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  Client, PostFilters, OpenDateRange,
} from '@/type';
import { LabelSelection } from '@/analysis/types';
import LabelFilterBtn from '@/analysis/label/LabelFilterBtn.vue';
import { emptySelection, isEmpty } from '@/analysis/label/selection';
import PersonsSidebarBtn from '@/analysis/person/PersonsSidebarBtn.vue';
import PostsToolbar from '../ResponsivePostsToolbar.vue';
import DateRangeFilter from './DateRangeFilter.vue';


export default defineComponent({
  emits: ['update:filters'],
  components: {
    PostsToolbar,
    DateRangeFilter,
    LabelFilterBtn,
    PersonsSidebarBtn,
  },
  props: {
    client: {
      type: Object as () => Client,
      required: true,
    },
    filters: {
      type: Object as () => PostFilters,
      required: true,
    },
    minimized: {
      type: Boolean,
      default: false,
    },
    maximizeBtn: Object,
  },
  data() {
    return {
      personIds: [] as number[],
      labelSelection: emptySelection() as LabelSelection,
    };
  },
  computed: {
    range(): OpenDateRange {
      return {
        start: this.filters.since,
        end: this.filters.until,
      };
    },
  },
  methods: {
    selectRange(range: OpenDateRange) {
      this.$emit('update:filters', {
        ...this.filters,
        since: range.start,
        until: range.end,
      });
    },
    onFiltersUpdate(filters: PostFilters) {
      if (this.personIds.length) {
        filters.personIds = this.personIds;
      }
      this.$emit('update:filters', this.withLabelSelection(filters));
    },
    onLabelSelectionChange(selection: LabelSelection) {
      this.labelSelection = selection;
      this.$emit('update:filters', this.withLabelSelection({ ...this.filters }));
    },
    withLabelSelection(filters: PostFilters): PostFilters {
      const {
        labelIds, labelCategoryIds, excludedLabelIds,
      } = this.labelSelection;

      delete filters.labelIds;
      delete filters.labelCategoryIds;
      delete filters.excludedLabelIds;

      if (isEmpty(this.labelSelection)) {
        return filters;
      }

      if (labelIds.length) {
        filters.labelIds = labelIds;
      }
      if (labelCategoryIds.length) {
        filters.labelCategoryIds = labelCategoryIds;
      }
      // Exclusions only mean something alongside a selected category.
      if (labelCategoryIds.length && excludedLabelIds.length) {
        filters.excludedLabelIds = excludedLabelIds;
      }

      return filters;
    },
    onPersonIdsChange(ids: number[]) {
      this.personIds = ids;
      const filters = { ...this.filters };
      if (ids.length) {
        filters.personIds = ids;
      } else {
        delete filters.personIds;
      }
      this.$emit('update:filters', filters);
    },
  },
});
</script>
